/* `useRecurso` — carregar dados de uma rota com os quatro estados que toda
 * tela precisa: carregando, pronto, vazio e erro.
 *
 * Existe porque o desenho trata os quatro como coisas diferentes (o componente
 * `Estado` do design system tem um tipo para cada), e porque a alternativa —
 * `useEffect` + três `useState` em cada tela — erra sempre nos mesmos dois
 * pontos:
 *
 *   * **Resposta fora de ordem.** Trocar de filtro rápido dispara duas buscas;
 *     se a primeira responder depois, a tela mostra o resultado do filtro
 *     antigo. Aqui cada busca leva um `AbortController` e a anterior é
 *     cancelada.
 *   * **Erro temporário indistinguível.** Banco fora do ar (503) permite
 *     "tentar de novo"; 403 não. O `ErroDaApi` carrega essa diferença, e a tela
 *     decide o que oferecer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useRecurso(buscar, dependencias = [], { ativo = true } = {}) {
  const [dados, definirDados] = useState(null);
  const [erro, definirErro] = useState(null);
  const [carregando, definirCarregando] = useState(ativo);

  const abortoRef = useRef(null);
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;

  const [gatilho, definirGatilho] = useState(0);
  const recarregar = useCallback(() => definirGatilho((n) => n + 1), []);

  useEffect(() => {
    if (!ativo) {
      definirCarregando(false);
      return undefined;
    }

    // Cancela a busca anterior: sem isto, a resposta de um filtro já trocado
    // pode chegar depois e sobrescrever a atual.
    abortoRef.current?.abort();
    const controle = new AbortController();
    abortoRef.current = controle;

    let vivo = true;
    definirCarregando(true);

    buscarRef
      .current(controle.signal)
      .then((resultado) => {
        if (!vivo || controle.signal.aborted) return;
        definirDados(resultado);
        definirErro(null);
      })
      .catch((falha) => {
        if (!vivo || falha.name === 'AbortError') return;
        definirErro(falha);
      })
      .finally(() => {
        if (!vivo || controle.signal.aborted) return;
        definirCarregando(false);
      });

    return () => {
      vivo = false;
      controle.abort();
    };
    // `buscar` fica de fora de propósito: ela é recriada a cada render, e
    // incluí-la faria a busca rodar em laço. Quem controla é `dependencias`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencias, gatilho, ativo]);

  return { dados, erro, carregando, recarregar, definirDados };
}
